import { useSelector } from 'react-redux';
import { displaybleBillingAndPremiumKeys } from '../../Home/helpers';
import {
  activeLoanHighlightedKeys,
  activeLoanKeys,
  loanCapacityHighlightedKeys,
  loanCapacityKeys,
} from '../../Home/constants';
import { formatCurrency, formatDecimal } from '../../../utils';
import { getPolicyTitleValue } from '../../PolicyDetails/hooks/policyDetails';

export const getLoanHighlightedTitleValue = (key: any, policyLoanData: any) => {
  if (!policyLoanData || !(key in policyLoanData)) {
    return { title: '', value: '' };
  }

  // Separate check for 'valueAvailable': allow 0, but reject null/undefined/''
  if (key === 'valueAvailable') {
    const value = policyLoanData[key];
    if (
      policyLoanData[key] === null ||
      policyLoanData[key] === undefined ||
      policyLoanData[key] === '' ||
      (typeof policyLoanData[key] === 'number' && policyLoanData[key] < 0)
    ) {
      return { title: '', value: '' };
    }
    return {
      title: 'Available Loan Value',
      value: formatCurrency(value),
    };
  }

  // General check for all other keys (0 is invalid here)
  if (
    policyLoanData[key] === null ||
    policyLoanData[key] === undefined ||
    policyLoanData[key] === '' ||
    (typeof policyLoanData[key] === 'number' && policyLoanData[key] <= 0)
  ) {
    return { title: '', value: '' };
  }

  // Switch for other known keys
  switch (key) {
    case 'loanBalance':
      return {
        title: 'Current Loan Balance',
        value: formatCurrency(policyLoanData[key]),
      };
    case 'loanPayOff':
      return {
        title: 'Current Loan Payoff',
        value: formatCurrency(policyLoanData[key]),
      };
    default:
      return { title: '', value: '' };
  }
};

export const getLoanCapacityHighlightedTitleValue = (
  key: any,
  policyLoanData: any,
) => {
  const value = policyLoanData?.[key];

  if (
    key === 'valueAvailable' &&
    value !== null &&
    value !== undefined &&
    value !== '' &&
    !(typeof value === 'number' && value < 0)
  ) {
    return {
      title: 'Available Loan Value',
      value: formatCurrency(value),
    };
  }

  if (key === 'loanBalance') {
    return {
      title: 'Current Loan Balance',
      value: formatCurrency(value ?? 0),
    };
  }

  if (key === 'loanPayOff') {
    return {
      title: 'Current Loan Payoff',
      value: formatCurrency(value ?? 0),
    };
  }

  return { title: '', value: '' }; // Always return an object to prevent UI crashes
};

export const getActiveLoanTitleValue = (key: any, policyLoanData: any) => {
  const value = policyLoanData?.[key];

  // Generic check: skip null, undefined, empty string, or non-positive numbers
  const isInvalid =
    value === null ||
    value === undefined ||
    value === '' ||
    (typeof value === 'number' && value <= 0);

  if (isInvalid) return null;

  switch (key) {
    case 'interestMethod':
      return {
        title: 'Interest Method (Advance, Arrears)',
        value: value,
      };
    case 'interestRateCharged':
      return {
        title: 'Interest Rate Charged on Loan',
        value: `${formatDecimal(value)}%`,
      };
    case 'interestRateCrtd':
      return {
        title: 'Interest Rate Credited on Loan Amount',
        value: `${formatDecimal(value)}%`,
      };
    case 'netInterestRate':
      return {
        title: 'Net Loan Rate',
        value: `${formatDecimal(value)}%`,
      };
    case 'interestRatePaid':
      return {
        title: 'Loan Interest Rate Paid',
        value: `${formatDecimal(value)}%`,
      };
    case 'typeOfInterestRateCharged':
      return {
        title: 'Interest Rate Type',
        value: value,
      };
    case 'prefLoanInterestFactor':
      return {
        title: 'Preferred Loan Interest Rate Factor',
        value: `${formatDecimal(value)}%`,
      };
    default:
      return null;
  }
};

export const getLoanCapacityTitleValue = (key: any, policyLoanData: any) => {
  const value = policyLoanData?.[key];

  // Early exit: skip null, undefined, '', or 0 (for numbers)
  if (
    value === null ||
    value === undefined ||
    value === '' ||
    (typeof value === 'number' && value <= 0)
  ) {
    return { title: '', value: '' };
  }

  switch (key) {
    case 'interestRateCharged':
      return {
        title: 'Interest Rate Charged on Loan',
        value: `${formatDecimal(value)}%`,
      };
    case 'interestMethod':
      return {
        title: 'Interest Method (Advance, Arrears)',
        value: value,
      };
    case 'interestRateCrtd':
      return {
        title: 'Interest Rate Credited on Loan Amount',
        value: `${formatDecimal(value)}%`,
      };
    case 'netInterestRate':
      return {
        title: 'Net Loan Rate',
        value: `${formatDecimal(value)}%`,
      };
    default:
      return { title: '', value: '' };
  }
};

export const getLoanHighlightedSectionData = (policyLoan, allowLoanCard) => {
  const currentLoanBalance = Number.parseFloat(policyLoan?.loanBalance ?? '0');

  const loansHighlightedDataFilteredObject = {};
  let loansHighlightedData: any[] = [];

  if (!allowLoanCard || !policyLoan || Object.keys(policyLoan).length === 0) {
    return [];
  }

  if (currentLoanBalance > 0) {
    // Filter active highlighted keys
    activeLoanHighlightedKeys.forEach((key) => {
      if (key === 'valueAvailable') {
        // Include valueAvailable even when null (e.g. Max Loan scenario), defaulting to 0
        loansHighlightedDataFilteredObject[key] = policyLoan[key] ?? 0;
      } else if (policyLoan[key] !== undefined && policyLoan[key] !== null) {
        loansHighlightedDataFilteredObject[key] = policyLoan[key];
      }
    });

    loansHighlightedData = Object.keys(loansHighlightedDataFilteredObject)
      .map((item) =>
        getLoanHighlightedTitleValue(item, loansHighlightedDataFilteredObject),
      )
      .filter((item) => item !== null); // Skip invalid fields
  } else {
    // Filter loan capacity highlighted keys; default null/undefined to 0 so $0.00 is displayed
    loanCapacityHighlightedKeys.forEach((key) => {
      loansHighlightedDataFilteredObject[key] = policyLoan[key] ?? 0;
    });

    loansHighlightedData = Object.keys(loansHighlightedDataFilteredObject)
      .map((item) =>
        getLoanCapacityHighlightedTitleValue(
          item,
          loansHighlightedDataFilteredObject,
        ),
      )
      .filter((item) => item !== null); // Skip invalid fields
  }

  return loansHighlightedData;
};

export const getPolicyLoanSectionData = (policyLoan, allowLoanCard) => {
  const currentLoanBalance = Number.parseFloat(policyLoan?.loanBalance ?? '0');

  const loansSectionFilteredObject = {};
  let loansSectionData: any = [];

  // Check if the loan card is allowed and if the loan balance is greater than 0
  if (allowLoanCard && currentLoanBalance > 0) {
    // Filter the relevant keys for active loan data
    activeLoanKeys.forEach((key) => {
      if (policyLoan?.[key] != null) {
        loansSectionFilteredObject[key] = policyLoan[key];
      }
    });

    // Map the filtered keys to their respective values (if valid)
    loansSectionData = Object.keys(loansSectionFilteredObject)
      .map((item) => {
        const value = getActiveLoanTitleValue(item, loansSectionFilteredObject);
        return value ?? null; // Ensure that null fields are skipped
      })
      .filter((item) => item !== null); // Filter out any null values

    // Check if the loan card is allowed and the loan balance is 0
  } else if (allowLoanCard && currentLoanBalance === 0) {
    // Filter the relevant keys for loan capacity data
    loanCapacityKeys.forEach((key) => {
      if (policyLoan?.[key] != null) {
        loansSectionFilteredObject[key] = policyLoan[key];
      }
    });

    // Map the filtered keys to their respective values (if valid)
    loansSectionData = Object.keys(loansSectionFilteredObject)
      .map((item) => {
        const value = getLoanCapacityTitleValue(
          item,
          loansSectionFilteredObject,
        );
        return value ?? null; // Ensure that null fields are skipped
      })
      .filter((item) => item !== null); // Filter out any null values
  }

  return loansSectionData;
};

export const usePolicyFinancialsSelector = () => {
  const policyData = useSelector((state: any) => state.policies);

  const {
    getPoliciesListData = {},
    isError = false,
    loading = true,
  } = policyData ?? {};

  const { policyHighlights = [], account = {} } =
    getPoliciesListData?.data?.policySummary ?? {};

  const {
    generalPolicyData = {},
    accountConfig = {},

    policyLoan = {},
  } = policyHighlights[0] ?? {};

  const { render: generalPolicyDataRender } = generalPolicyData ?? {};
  const allowFinancialActivities = accountConfig.allowFinancialActivities;

  const filteredObject = {};
  displaybleBillingAndPremiumKeys.forEach((key) => {
    if (generalPolicyData[key] && generalPolicyDataRender?.[key]) {
      filteredObject[key] = generalPolicyData[key];
    }
  });

  const billingAndPremiumData = Object.keys(filteredObject).map((item) =>
    getPolicyTitleValue(item, filteredObject),
  );

  // Loans AsOfDate Value
  let loansAsOfDate = '';
  loansAsOfDate = policyLoan?.asOfDate;
  //Loans Information Highlighted Section
  let loanHighlightedFormattedData: any = [];
  loanHighlightedFormattedData = getLoanHighlightedSectionData(
    policyLoan,
    accountConfig?.allowLoanCard,
  );

  //Loans Information Sub Section
  let policyLoanFormattedData: any = [];
  policyLoanFormattedData = getPolicyLoanSectionData(
    policyLoan,
    accountConfig?.allowLoanCard,
  );

  return {
    isError,
    loading,
    billingAndPremiumData,
    account,
    accessToken: policyData?.token,
    policyNumber: getPoliciesListData?.data?.policySummary?.account?.number,
    policyLoanData: policyLoanFormattedData,
    loanHighlightedData: loanHighlightedFormattedData,
    loansAsOfDate: loansAsOfDate,
    allowFinancialActivities: allowFinancialActivities,
  };
};
